import { UnifiedTreeNode, SourceData } from '@/app/review/context/ReviewContext';
import { Database } from "@/types/supabase";
import { TreeViewRow } from '@/types/treeView';

type FileUploadRow = Database["public"]["Tables"]["file_uploads"]["Row"];
type DocumentStagingRow = Database["public"]["Tables"]["document_staging"]["Row"];
type LocationRow = Database["public"]["Tables"]["locations"]["Row"];

export interface FlatUnifiedTreeNode {
  node: UnifiedTreeNode;
  level: number;
  hasChildren: boolean;
}

export function flattenUnifiedTreeList(
  nodes: UnifiedTreeNode[],
  expandedIds: Set<string>, // node ids that are expanded
  level = 0
): FlatUnifiedTreeNode[] {
  let flatList: FlatUnifiedTreeNode[] = [];
  nodes.forEach((node) => {
    // Chevron only when there is something renderable underneath (files are
    // not rendered as rows in the locations tree).
    const hasChildren = node.children.some(
      (child) => child.nodeType === 'UI_LOCATION_FOLDER'
    );

    flatList.push({ node, level, hasChildren });
    if (hasChildren && expandedIds.has(node.id)) {
      flatList = flatList.concat(
        flattenUnifiedTreeList(node.children, expandedIds, level + 1)
      );
    }
  });
  return flatList;
}

// Prune the tree to nodes whose name matches the query (case-insensitive),
// keeping the ancestor chain of every match. A directly-matching node keeps
// its original subtree so it can still be explored. Returns the ids of every
// kept folder so the caller can render the matches expanded.
export function filterUnifiedTreeList(
  nodes: UnifiedTreeNode[],
  query: string
): { tree: UnifiedTreeNode[]; expandedIds: Set<string> } {
  const q = query.toLowerCase();
  const expandedIds = new Set<string>();

  const prune = (list: UnifiedTreeNode[]): UnifiedTreeNode[] => {
    const kept: UnifiedTreeNode[] = [];
    for (const node of list) {
      // Files are not rendered as rows in the locations tree, so they
      // neither match nor keep their parent alive.
      if (node.nodeType === 'FILE_ITEM') continue;
      const selfMatch = node.name.toLowerCase().includes(q);
      const children = prune(node.children);
      if (selfMatch || children.length > 0) {
        kept.push(selfMatch ? node : { ...node, children });
        expandedIds.add(node.id);
      }
    }
    return kept;
  };

  return { tree: prune(nodes), expandedIds };
}

type AggregatedStatus = UnifiedTreeNode['aggregatedFileStatus'];

function containerStatus(row: TreeViewRow): { status: AggregatedStatus; progress: number } {
  switch (row.processing_status) {
    case 'failed':
    case 'Failed':
      return { status: 'error', progress: row.processing_progress ?? 0 };
    case 'processing':
      return { status: 'in_progress', progress: row.processing_progress ?? 0 };
    case 'pending':
      // a processed location whose staging rows are gone is still complete
      if (row.node_type === 'location' && row.location_processed) {
        return { status: 'completed', progress: 100 };
      }
      return { status: 'preparing', progress: 0 };
    case 'completed':
    default:
      return { status: 'completed', progress: 100 };
  }
}

function fileStatus(row: TreeViewRow): { status: AggregatedStatus; progress: number } {
  if (row.processing_progress === 100 || row.processing_status === 'completed') {
    return { status: 'completed', progress: 100 };
  }
  switch (row.processing_status) {
    case 'failed':
    case 'Failed':
      return { status: 'error', progress: row.processing_progress ?? 0 };
    case 'pending':
    case null:
      return { status: 'preparing', progress: 0 };
    default:
      return { status: 'in_progress', progress: row.processing_progress ?? 0 };
  }
}

// Build the locations tree from tree_view rows. Node identity is the
// tree_nodes uuid ("file:<id>" for files) and is stable across rename/move;
// fullPath is derived for display and drag-drop checks.
export function buildTreeFromTreeView(
  rows: TreeViewRow[]
): { tree: UnifiedTreeNode[]; map: Map<string, UnifiedTreeNode> } {
  const nodeMap = new Map<string, UnifiedTreeNode>();

  rows.forEach((row) => {
    const isFile = row.node_type === 'file';
    const { status, progress } = isFile ? fileStatus(row) : containerStatus(row);

    const sourceData: SourceData = {};

    if (row.location_id) {
      sourceData.dbLocation = {
        id: row.location_id,
        name: row.name,
        user_id: row.user_id,
        bui: row.bui,
        address: row.location_address,
        status: row.location_status,
        is_verified: row.location_verified,
        isProcessed: row.location_processed,
        validated: row.validated,
        latitude: row.latitude,
        longitude: row.longitude,
        city: row.city,
        state: row.state,
        country: row.country,
        postal_code: row.postal_code,
        sqft: row.sqft,
        baseRent: row.base_rent,
        additionalRent: row.additional_rent,
        expirationDate: row.expiration_date,
        landlord: row.landlord,
        rent_schedule: row.rent_schedule,
        doc_hierarchy: row.doc_hierarchy,
        docs: row.doc_count,
        document_group_id: row.document_group_id,
        created_at: row.created_at,
        updated_at: row.updated_at,
      } as LocationRow;
    }

    if (row.file_upload_id) {
      sourceData.fileUpload = {
        id: row.file_upload_id,
        file: row.file_name,
        user_id: row.user_id,
        document_group_id: row.document_group_id,
        created_at: row.created_at,
        updated_at: row.updated_at,
      } as FileUploadRow;

      if (row.processing_status) {
        sourceData.documentStaging = {
          status: row.processing_status,
          progress: row.processing_progress,
          file_upload_id: row.file_upload_id,
          document_group_id: row.document_group_id,
        } as DocumentStagingRow;
      }
    }

    nodeMap.set(row.node_id, {
      id: row.node_id,
      parentNodeId: row.parent_id,
      name: row.name,
      pathSegments: [], // filled in below once parents are linked
      fullPath: '',
      nodeType: isFile ? 'FILE_ITEM' : 'UI_LOCATION_FOLDER',
      sourceData,
      children: [],
      isExpanded: false,
      isShared: row.is_shared,
      docCount: row.doc_count || 0,
      directFileCount: 0,
      aggregatedFileStatus: status,
      aggregatedFileProgress: progress,
      contractAstStatus: (row.contract_ast_status as UnifiedTreeNode['contractAstStatus']) || null,
      isClickable: !!row.location_id && row.location_processed === true,
      validated: row.validated,
      created_at: row.created_at || undefined,
      updated_at: row.updated_at || undefined,
    });
  });

  // Link children. Containers whose parent is not visible become roots;
  // files whose parent folder was filtered out (e.g. a team subtree) are
  // dropped rather than surfacing at the root of the personal tree.
  const roots: UnifiedTreeNode[] = [];
  nodeMap.forEach((node) => {
    const parent = node.parentNodeId ? nodeMap.get(node.parentNodeId) : undefined;
    if (parent) {
      parent.children.push(node);
    } else if (node.parentNodeId && node.nodeType === 'FILE_ITEM') {
      nodeMap.delete(node.id);
    } else {
      roots.push(node);
    }
  });

  // Prune upload artifacts. Drag-dropping folders on the upload page creates
  // document_groups/file_uploads (and, via DB triggers, tree_nodes) the moment
  // files land in S3 — before the user ever clicks "Add to Urbis". Those
  // partially-uploaded subtrees must not appear here as if they were real
  // locations. A folder subtree is an artifact when it contains files but
  // none of them were ever submitted (no document_staging → the file rows'
  // processing_status is null) and no location exists anywhere under it.
  // Folders with NO files at all are kept — those are intentionally created
  // empty folders (via "New Folder"), not upload leftovers.
  const pruneUploadArtifacts = (
    nodes: UnifiedTreeNode[]
  ): { hasLocation: boolean; hasSubmitted: boolean; hasFile: boolean } => {
    const rollup = { hasLocation: false, hasSubmitted: false, hasFile: false };
    for (let i = nodes.length - 1; i >= 0; i--) {
      const node = nodes[i];
      if (node.nodeType === 'FILE_ITEM') {
        rollup.hasFile = true;
        if (node.sourceData.documentStaging) rollup.hasSubmitted = true;
        continue;
      }
      const childInfo = pruneUploadArtifacts(node.children);
      const isLocation = !!node.sourceData.dbLocation;
      const keep =
        isLocation || childInfo.hasLocation || childInfo.hasSubmitted || !childInfo.hasFile;
      if (!keep) {
        nodes.splice(i, 1);
        const removeFromMap = (n: UnifiedTreeNode) => {
          nodeMap.delete(n.id);
          n.children.forEach(removeFromMap);
        };
        removeFromMap(node);
        // Propagate hasFile so an ancestor that contained ONLY this artifact
        // subtree is recognized as an artifact too, not as an "empty folder".
        rollup.hasFile = rollup.hasFile || childInfo.hasFile;
        continue;
      }
      rollup.hasLocation = rollup.hasLocation || isLocation || childInfo.hasLocation;
      rollup.hasSubmitted = rollup.hasSubmitted || childInfo.hasSubmitted;
      rollup.hasFile = rollup.hasFile || childInfo.hasFile;
    }
    return rollup;
  };
  pruneUploadArtifacts(roots);

  // Derive paths root-down and count direct files.
  const assignPaths = (nodes: UnifiedTreeNode[], parentSegments: string[]) => {
    nodes.forEach((node) => {
      node.pathSegments = [...parentSegments, node.name];
      node.fullPath = node.pathSegments.join('/');
      node.directFileCount = node.children.filter(
        (c) => c.nodeType === 'FILE_ITEM'
      ).length;
      if (node.sourceData.dbLocation) {
        (node.sourceData.dbLocation as LocationRow & { group?: string | null }).group =
          node.fullPath;
      }
      assignPaths(node.children, node.pathSegments);
    });
  };
  assignPaths(roots, []);

  const sortNodes = (a: UnifiedTreeNode, b: UnifiedTreeNode) => {
    const aIsFolder = a.nodeType !== 'FILE_ITEM';
    const bIsFolder = b.nodeType !== 'FILE_ITEM';
    if (aIsFolder !== bIsFolder) return aIsFolder ? -1 : 1;
    return a.name.localeCompare(b.name);
  };

  nodeMap.forEach((node) => {
    if (node.children.length > 1) node.children.sort(sortNodes);
  });
  roots.sort(sortNodes);

  return { tree: roots, map: nodeMap };
}

export function generateDebugTreeString(nodes: UnifiedTreeNode[], level = 0): string {
  let debugString = "";
  const indent = "  ".repeat(level);

  nodes.forEach(node => {
    debugString += `${indent}- ${node.name} [${node.nodeType}] id=${node.id} path=${node.fullPath} status=${node.aggregatedFileStatus}\n`;
    if (node.children.length > 0) {
      debugString += generateDebugTreeString(node.children, level + 1);
    }
  });

  return debugString;
}
